import classNames from 'classnames';
import React, { useState } from 'react';
import { FaEuroSign, FaRegTrashAlt } from 'react-icons/fa';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { MdAdd } from 'react-icons/md';
import { Button } from 'rizzui';
import CustomSelect from '../../../components/forms/CustomSelect';
import CustomSelectApiHookForm from '../../../components/forms/CustomSelectApiHookForms';
import RequiredInput from '../../../components/forms/InputHOC';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useProducts from '../../../hooks/api-calls/useProducts';
import FormGroup from '../../../layout/shared/form-group';
import { paymentMethods } from '../TutorForm';

const SUBSIDISED_PRODUCT_TYPE_ID = '26a41eb6-a9d1-4d2f-88e5-1bb59bdb9ae1';
const BONIFIED_TYPE_NAMES = ['CursoBonificado', 'Bonificada'];

interface TutorProductsFormProps {
    formik: any;
    className?: string;
}

const isBonifiedProductOption = (option: any) => {
    if (!option) return false;
    if (option.productTypeId === SUBSIDISED_PRODUCT_TYPE_ID) return true;
    const typeName = option.productTypeName ?? option.object?.productTypeName ?? '';
    return BONIFIED_TYPE_NAMES.includes(typeName);
};

const TutorProductsForm: React.FC<TutorProductsFormProps> = ({ formik, className }) => {
    const { fetchProducts } = useProducts({ active: true });
    const { userCan } = usePrivilege();

    const [newProduct, setNewProduct] = useState<{ id: string; price: string; paymentMethod: string; name: string }>({
        id: '',
        price: '',
        paymentMethod: '',
        name: '',
    });

    const assignedProductIds = formik.values.products.map((p: any) => p?.id).filter(Boolean);

    const filterBonifiedProducts = (options: any[] | undefined, excludeIds: string[] = assignedProductIds) =>
        (options ?? [])
            .filter(isBonifiedProductOption)
            .filter((o: any) => !excludeIds.includes(o.value));

    const newProductFormikShim = {
        values: { newProductId: newProduct.id || null },
        setFieldValue: (_: string, value: any) => {
            setNewProduct((prev) => ({ ...prev, id: value ?? '', name: prev.name }));
        },
    };

    const handleRemove = (index: number) => {
        const updated = formik.values.products.filter((_: any, i: number) => i !== index);
        formik.setFieldValue('products', updated);
    };

    const addNewProduct = () => {
        if (!newProduct.id) return;
        if (formik.values.tutorType === 'external' && (!newProduct.price || !newProduct.paymentMethod)) {
            return;
        }
        formik.setFieldValue('products', [...formik.values.products, { ...newProduct }]);
        setNewProduct({ id: '', price: '', paymentMethod: '', name: '' });
    };

    return (
        <FormGroup
            title="Acciones bonificadas"
            description="Acciones formativas bonificadas que el tutor puede impartir."
            className={classNames('pt-6', className, { hidden: formik.values.tutorType === '' })}
            titleCols="@md:col-span-2"
            childCols="@md:col-span-10 md:grid-cols-1"
        >
            {formik.values.products.map((productItem: any, index: number) => {
                const productErrors = formik.errors.products && formik.errors.products[index];
                return (
                    <div
                        key={productItem.tutorHasProductId ?? productItem.id ?? index}
                        className="grid grid-cols-2 md:grid-cols-6 gap-4 pb-4 items-end border-b border-gray-200"
                    >
                        <div className="col-span-2 space-y-2">
                            <CustomSelectApiHookForm
                                id={`products[${index}].id`}
                                label="Acción bonificada"
                                formik={formik}
                                fetchOptions={async (searchText: string | null) => {
                                    const text = searchText || formik.values.products[index]?.name;
                                    const options = await fetchProducts({ search_text: text }) as any;
                                    const otherAssigned = assignedProductIds.filter((id: string) => id !== productItem?.id);
                                    return filterBonifiedProducts(options, otherAssigned);
                                }}
                                error={productErrors?.id}
                                minInputLength={3}
                                containerClassName=""
                                isDisabled={!userCan('get_products_for_select', 'products')}
                            />
                        </div>

                        {formik.values.tutorType === 'external' && (
                            <RequiredInput
                                id={`products[${index}].price`}
                                type="number"
                                label="Precio"
                                suffix={<FaEuroSign />}
                                value={productItem.price}
                                error={productErrors?.price}
                                formik={formik}
                            />
                        )}

                        {formik.values.tutorType === 'external' && (
                            <CustomSelect
                                id={`products[${index}].paymentMethod`}
                                required
                                label="Método de pago"
                                options={paymentMethods}
                                onChange={formik.handleChange}
                                value={{
                                    value: productItem.paymentMethod,
                                    label: paymentMethods.find((p: any) => p.value === productItem.paymentMethod)?.label,
                                }}
                                error={productErrors?.paymentMethod}
                            />
                        )}

                        <div className="flex justify-end items-end">
                            <Button type="button" color="danger" size="sm" onClick={() => handleRemove(index)}>
                                <FaRegTrashAlt />
                            </Button>
                        </div>
                    </div>
                );
            })}

            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-4">
                <div className="col-span-2 space-y-2">
                    <CustomSelectApiHookForm
                        id="newProductId"
                        label="Nueva acción bonificada"
                        formik={newProductFormikShim}
                        fetchOptions={async (searchText: string | null) => {
                            const options = await fetchProducts({ search_text: searchText });
                            return filterBonifiedProducts(options);
                        }}
                        setValue={(value: any) => {
                            setNewProduct({ ...newProduct, id: value.id, name: value?.object?.name });
                        }}
                        error={undefined}
                        minInputLength={3}
                        containerClassName=""
                        isDisabled={!userCan('get_products_for_select', 'products')}
                    />
                    {!userCan('get_products_for_select', 'products') && (
                        <div className="flex items-center gap-2 text-red-500 text-sm">
                            <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                            <span>No tienes permisos para ver los productos</span>
                        </div>
                    )}
                </div>

                {formik.values.tutorType === 'external' && (
                    <RequiredInput
                        id="price"
                        type="number"
                        label="Precio"
                        suffix={<FaEuroSign />}
                        value={newProduct.price}
                        onChange={(e: any) => setNewProduct({ ...newProduct, price: e.target.value })}
                        formik={formik}
                    />
                )}

                {formik.values.tutorType === 'external' && (
                    <CustomSelect
                        id="paymentMethod"
                        isClearable
                        required
                        label="Método de pago"
                        options={paymentMethods}
                        onChange={(e: any) => setNewProduct({ ...newProduct, paymentMethod: e?.value ?? '' })}
                        value={newProduct.paymentMethod
                            ? { value: newProduct.paymentMethod, label: paymentMethods.find((p: any) => p.value === newProduct.paymentMethod)?.label ?? newProduct.paymentMethod }
                            : null}
                    />
                )}

                <div className="flex justify-end items-end">
                    <Button
                        type="button"
                        color="secondary"
                        size="sm"
                        disabled={newProduct.id === '' || (formik.values.tutorType === 'external' && (newProduct.price === '' || !newProduct.paymentMethod))}
                        onClick={addNewProduct}
                    >
                        <MdAdd className="h-5 w-5" />
                    </Button>
                </div>
            </div>
        </FormGroup>
    );
};

export default TutorProductsForm;
